import { useState } from 'react'
import { useGiveawayTypes } from '@/hooks/queries'
import { entryOptionLabel, isFirstEntries, type GiveawayObjective } from '@/models/giveaway'
import { NewObjective } from './NewObjective'
import { ToggleField } from '../ui/ToggleField'
import { ReferralIcon } from '../giveaway/ReferralIcon'
import type { StepProps } from './types'

interface ObjectivesStepProps extends StepProps {
  onBeforeLeave: () => void
}

/** Port of components/new_giveaway/pages/objectives.vue */
export function ObjectivesStep({
  giveaway,
  update,
  onBack,
  onNext,
  onBeforeLeave,
}: ObjectivesStepProps) {
  const [adding, setAdding] = useState(false)
  const { data: giveawayTypes } = useGiveawayTypes()
  const firstEntries = isFirstEntries(giveaway, giveawayTypes)
  const hasReferral = giveaway.giveaway_objective.some((objective) => objective.is_referral)
  // Referrals are credited when someone enters another way, so there has to
  // be another way.
  const hasDirectOption = giveaway.giveaway_objective.some((objective) => !objective.is_referral)

  const addObjective = (objective: GiveawayObjective) => {
    update((draft) => {
      draft.giveaway_objective = [...draft.giveaway_objective, objective]
    })
    setAdding(false)
  }

  const removeObjective = (key: string) => {
    update((draft) => {
      draft.giveaway_objective = draft.giveaway_objective.filter(
        (objective) => objective.key !== key,
      )
    })
  }

  if (adding) {
    return (
      <NewObjective
        onCancel={() => setAdding(false)}
        onCreate={addObjective}
        onBeforeLeave={onBeforeLeave}
        hasReferral={hasReferral}
        firstEntries={firstEntries}
      />
    )
  }

  return (
    <div className="content-section">
      <div className="field-row with-margin large-margin center" style={{ marginTop: 50 }}>
        <div className="field half button">
          <button type="button" className="field-box" onClick={() => setAdding(true)}>
            <div className="field-box-prepend">
              <img src="/icons/add_objective.png" alt="" />
            </div>
            <div className="field-box-content upper">Add New Option</div>
          </button>
        </div>
      </div>

      <div className="content-row left with-margin upper">Entry Options</div>

      <div className="fields with-margin large-margin">
        {giveaway.giveaway_objective.length === 0 ? (
          <div className="content-row small upper">
            No entry options yet — add at least one so people can enter.
          </div>
        ) : !hasDirectOption ? (
          <div className="content-row small upper">
            Add another entry option too — referred people need a way to enter.
          </div>
        ) : null}

        {giveaway.giveaway_objective.map((objective) => (
          <div className="field-row with-margin small-margin" key={objective.key}>
            <div className="field">
              <div className="field-box">
                <div className="field-box-prepend">
                  {objective.is_referral ? (
                    <ReferralIcon />
                  ) : (
                    <img src="/icons/discord.png" alt="" />
                  )}
                </div>
                <div className="field-box-content upper small">{objective.display_name}</div>
                <div className="field-box-content small">
                  {entryOptionLabel(objective, firstEntries, giveaway.require_all_objectives)}
                </div>
                <button
                  type="button"
                  className="field-box-append"
                  aria-label={`Remove ${objective.display_name}`}
                  onClick={() => removeObjective(objective.key)}
                >
                  <img src="/icons/delete_objective.png" alt="" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {firstEntries ? (
        <ToggleField
          label="Require every entry option to claim a key"
          checked={giveaway.require_all_objectives}
          onChange={(checked) =>
            update((draft) => {
              draft.require_all_objectives = checked
            })
          }
          hint="Off: completing any one option claims a key. On: people have to complete every option first. Either way, each person gets one key."
        />
      ) : null}

      <div className="footer">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onBack}>
            Back
          </button>
          <button type="button" className="cta next" onClick={onNext} disabled={!hasDirectOption}>
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
